import { DEFAULT_FIB_EXTENSION_LEVELS, DEFAULT_FIB_LEVELS } from "./renderCanvas";
function isFinitePrice(price) {
    return typeof price === "number" && Number.isFinite(price);
}
function addPriceMarker(markers, seen, key, price) {
    if (!isFinitePrice(price)) {
        return;
    }
    const normalized = price.toFixed(8);
    if (seen.has(normalized)) {
        return;
    }
    seen.add(normalized);
    markers.push({ key, price });
}
function collectPointPriceMarkers(drawing, points, keyPrefix) {
    const markers = [];
    const seen = new Set();
    points.forEach((point, index) => {
        addPriceMarker(markers, seen, `${drawing.id}-${keyPrefix}-${index}`, point.y);
    });
    return markers;
}
function resolveFibonacciMarkers(drawing) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return [];
    }
    const markers = [];
    const seen = new Set();
    const levels = drawing.fibLevels ?? DEFAULT_FIB_LEVELS;
    const priceDelta = endPoint.y - startPoint.y;
    levels.forEach((level, index) => {
        addPriceMarker(markers, seen, `${drawing.id}-fib-${index}`, startPoint.y + priceDelta * level);
    });
    return markers;
}
function resolveFibExtensionMarkers(drawing) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return [];
    }
    const markers = [];
    const seen = new Set();
    const levels = drawing.fibLevels ?? DEFAULT_FIB_EXTENSION_LEVELS;
    const priceDelta = endPoint.y - startPoint.y;
    levels.forEach((level, index) => {
        addPriceMarker(markers, seen, `${drawing.id}-fib-extension-${index}`, endPoint.y + priceDelta * level);
    });
    return markers;
}
export function resolveDrawingPriceMarkers(drawing) {
    if (!drawing || drawing.visible === false) {
        return [];
    }
    switch (drawing.type) {
        case "trendLine":
        case "ray":
        case "extendedLine":
        case "arrow":
        case "polyline":
        case "text": {
            const lastPoint = drawing.points[drawing.points.length - 1];
            return lastPoint ? [{ key: `${drawing.id}-anchor`, price: lastPoint.y }] : [];
        }
        case "hLine": {
            const firstPoint = drawing.points[0];
            return firstPoint ? [{ key: `${drawing.id}-hline`, price: firstPoint.y }] : [];
        }
        case "rectangle":
        case "dateAndPriceRange":
        case "channel":
        case "parallelChannel":
        case "pitchfork":
        case "abcdPattern":
        case "fibArc":
        case "regressionChannel":
            return collectPointPriceMarkers(drawing, drawing.points, drawing.type);
        case "longPosition":
        case "shortPosition": {
            const riskReward = drawing.riskReward;
            if (!riskReward) {
                return collectPointPriceMarkers(drawing, drawing.points, drawing.type);
            }
            return [
                { key: `${drawing.id}-entry`, price: riskReward.entry },
                { key: `${drawing.id}-stop`, price: riskReward.stop },
                { key: `${drawing.id}-target`, price: riskReward.target },
            ];
        }
        case "fibonacci":
            return resolveFibonacciMarkers(drawing);
        case "fibExtension":
            return resolveFibExtensionMarkers(drawing);
        case "vLine":
        case "fibTimeZone":
            return [];
        default:
            return collectPointPriceMarkers(drawing, drawing.points, drawing.type);
    }
}
// NOTE (Phase 1, task 3.1): the legacy DrawingPriceLabels React component was removed.
// It was the only reason this module imported ../coordinates (PriceCoordinate, a legacy
// class component that needs GenericChartComponent plus chartConfig from the old
// runtime) and nothing in production ever rendered it - the live path is
// resolveDrawingPriceMarkers, consumed by src/widget/DrawingPriceLabelsOverlay.tsx and
// src/widget/drawingPriceLabelInputs.ts. The three tests that only exercised that legacy
// render path went with it; the four marker-resolution tests stay.
